import { useLocation, useNavigate } from 'react-router-dom';
import {
  AccountBalance,
  Analytics,
  Assessment,
  AssignmentTurnedIn,
  AutoStories,
  Business,
  Chat,
  Explore,
  FactCheck,
  Headphones,
  Inventory,
  RateReview,
  Science,
  Search,
  Security,
  SmartToy,
  Transform,
  TrendingUp,
} from '@mui/icons-material';
import { Divider, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';

const navItems = [
  { path: '/admin-v2', label: 'Search', icon: Search, exact: true },
  { path: '/admin-v2/organizations', label: 'Organizations', icon: Business },
  { divider: true },
  { path: '/admin-v2/jotaudit', label: 'JotAudit', icon: Assessment },
  { path: '/admin-v2/intake-hub', label: 'Intake Hub', icon: AssignmentTurnedIn },
  { path: '/admin-v2/feedback', label: 'Feedback', icon: RateReview },
  { divider: true },
  { path: '/admin-v2/encounter-telemetry-sessions', label: 'Telemetry', icon: Analytics },
  { path: '/admin-v2/streaming-threads', label: 'Streaming Threads', icon: Chat },
  { divider: true },
  { path: '/admin-v2/capabilities', label: 'Capabilities', icon: Security },
  { path: '/admin-v2/bundles', label: 'Bundles', icon: Inventory },
  { divider: true },
  { path: '/admin-v2/shadow-notes', label: 'Shadow Notes', icon: Science },
  { path: '/admin-v2/feature-explorer', label: 'Feature Explorer', icon: Explore },
  { path: '/admin-v2/template-model-conversion', label: 'Template Conversion', icon: Transform },
  { divider: true },
  { path: '/admin-v2/prospects', label: 'Sales Prospects', icon: TrendingUp },
  { path: '/admin-v2/eligibility-workqueue', label: 'Eligibility Queue', icon: FactCheck },
  { path: '/admin-v2/billing-dashboard', label: 'Billing Dashboard', icon: AccountBalance },
  { divider: true },
  { path: '/admin-v2/agent', label: 'Agent', icon: SmartToy },
  { divider: true },
  { path: '/admin-v2/analytics-docs', label: 'Analytics Docs', icon: AutoStories },
];

export function AdminSidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string, exact?: boolean) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <aside className="w-60 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div
        className="h-16 flex items-center px-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
        onClick={() => navigate('/admin-v2')}
      >
        <Headphones className="h-8 w-8 text-blue-600" />
        <span className="ml-3 text-xl font-semibold text-gray-900">Admin v2</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2">
        <List component="nav" disablePadding>
          {navItems.map((item, index) => {
            if ('divider' in item) {
              return <Divider key={`divider-${index}`} sx={{ my: 1 }} />;
            }

            const Icon = item.icon;
            const active = isActive(item.path, item.exact);

            return (
              <ListItemButton
                key={item.path}
                onClick={() => navigate(item.path)}
                selected={active}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.50',
                    '&:hover': {
                      backgroundColor: 'primary.100',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Icon fontSize="small" color={active ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  slotProps={{
                    primary: {
                      color: active ? 'primary.main' : 'text.primary',
                      sx: {
                        fontSize: '0.875rem',
                        fontWeight: active ? 600 : 400,
                      },
                    },
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </nav>
    </aside>
  );
}
